import { useState } from 'react'
import type { FormEvent } from 'react'
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '../../lib/api'
import { useAuth } from '../../lib/auth'
import { useConfirm } from '../../lib/confirm'
import { formatDate, formatNumber, titleCase } from '../../lib/format'
import type { Site } from '../../types'
import { Alert, Button, Card, CardBody, CardHeader, Skeleton, StatusBadge, Textarea } from '../../components/ui'

interface DeleteRequest {
  id: string
  reason?: string
  requested_by?: string
  requested_at?: string
  status?: string
}

const DECISION_ROLES = new Set(['super_admin', 'gov_director'])

export default function SiteDetail() {
  const { siteId } = useParams<{ siteId: string }>()
  const { user } = useAuth()
  const qc = useQueryClient()
  const navigate = useNavigate()
  const confirm = useConfirm()
  const [showDeleteForm, setShowDeleteForm] = useState(false)
  const [reason, setReason] = useState('')

  const siteQuery = useQuery({
    queryKey: ['gov', 'site', siteId],
    queryFn: () => api.get<{ site: Site }>(`/sites/${siteId}`),
    enabled: Boolean(siteId),
  })
  const requestsQuery = useQuery({
    queryKey: ['gov', 'site-delete-requests', siteId],
    queryFn: () => api.get<{ ok: boolean; items: DeleteRequest[] }>(`/gov/sites/${siteId}/delete-requests`),
    enabled: Boolean(siteId) && DECISION_ROLES.has(user?.role ?? ''),
  })

  const requestDelete = useMutation({
    mutationFn: (r: string) => api.post(`/gov/sites/${siteId}/request-delete`, { reason: r }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['gov', 'site-delete-requests', siteId] })
      setShowDeleteForm(false)
      setReason('')
    },
  })
  const approve = useMutation({
    mutationFn: (id: string) => api.post(`/gov/delete-requests/${id}/approve`),
    // Approval permanently deletes the site (see gov.site_approve_delete),
    // so there is no longer a detail page to show. Previously this only
    // invalidated the delete-requests query, leaving the page rendering
    // stale cached site data with a "Request deletion" button reappearing
    // (since the now-empty pending list looked like "nothing requested
    // yet") - with no indication anywhere that the delete had happened.
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['gov', 'sites'] })
      navigate('/sites')
    },
  })
  const reject = useMutation({
    mutationFn: (id: string) => api.post(`/gov/delete-requests/${id}/reject`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['gov', 'site-delete-requests', siteId] }),
  })
  const cancel = useMutation({
    mutationFn: (id: string) => api.post(`/gov/delete-requests/${id}/cancel`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['gov', 'site-delete-requests', siteId] }),
  })

  if (!siteId) return <Navigate to="/sites" replace />
  if (siteQuery.isError) {
    return (
      <Alert tone="danger">
        {(siteQuery.error as Error).message}
        <div style={{ marginTop: 'var(--sp-3)' }}><Link to="/sites">Back to sites</Link></div>
      </Alert>
    )
  }
  if (siteQuery.isLoading || !siteQuery.data) return <div className="stack"><Skeleton h={30} w="50%" /><Skeleton h={140} /></div>

  const site = siteQuery.data.site
  const canDecide = DECISION_ROLES.has(user?.role ?? '')
  const pending = (requestsQuery.data?.items ?? [])[0]

  async function onRequestDelete(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (await confirm({ title: 'Request deletion of this site?', message: reason || undefined, destructive: true })) {
      requestDelete.mutate(reason)
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{site.name}</h1>
          <p className="page-head__sub">
            {[titleCase(site.district_id), titleCase(site.region_id)].filter(Boolean).join(', ') || 'Location not set'}
          </p>
        </div>
        <StatusBadge status={site.validation_status || 'pending'} />
      </div>

      <div className="grid grid--2" style={{ marginBottom: 'var(--sp-6)' }}>
        <Card>
          <CardHeader title="Details" />
          <CardBody>
            <div className="stack">
              <Row label="Minerals" value={(site.mineral_types ?? []).map(titleCase).join(', ') || '—'} />
              <Row label="Land tenure" value={titleCase(site.land_tenure_type) || '—'} />
              <Row label="Area" value={site.area_size_ha ? `${formatNumber(site.area_size_ha, 2)} ha` : '—'} />
              <Row label="Registered" value={formatDate(site.created_at)} />
            </div>
          </CardBody>
        </Card>
        <Card>
          <CardHeader title="Description" />
          <CardBody>
            <p style={{ margin: 0 }}>{site.description || 'No description provided.'}</p>
          </CardBody>
        </Card>
      </div>

      {canDecide && (
        <Card>
          <CardHeader title="Site deletion" sub="Requires a second authorised admin to approve." />
          <CardBody>
            {(requestDelete.isError || approve.isError || reject.isError || cancel.isError) && (
              <div style={{ marginBottom: 'var(--sp-3)' }}>
                <Alert tone="danger">
                  {[requestDelete.error, approve.error, reject.error, cancel.error]
                    .find((e) => e instanceof ApiError) instanceof ApiError
                    ? ((requestDelete.error ?? approve.error ?? reject.error ?? cancel.error) as ApiError).message
                    : 'That action failed.'}
                </Alert>
              </div>
            )}
            {pending ? (
              <div className="stack">
                <p style={{ margin: 0 }}>
                  <strong>Pending deletion request</strong> — {pending.reason}
                </p>
                <div className="row">
                  <Button variant="danger" loading={approve.isPending} onClick={async () => {
                    if (await confirm({
                      title: 'Permanently delete this site?',
                      message: 'This cannot be undone.',
                      confirmLabel: 'Delete site',
                      destructive: true,
                    })) approve.mutate(pending.id)
                  }}>
                    Approve &amp; delete
                  </Button>
                  <Button variant="secondary" loading={reject.isPending} onClick={async () => {
                    if (await confirm({ title: 'Reject this deletion request?' })) reject.mutate(pending.id)
                  }}>
                    Reject
                  </Button>
                  <Button variant="ghost" loading={cancel.isPending} onClick={async () => {
                    if (await confirm({ title: 'Cancel this deletion request?', destructive: true })) cancel.mutate(pending.id)
                  }}>
                    Cancel request
                  </Button>
                </div>
              </div>
            ) : showDeleteForm ? (
              <form onSubmit={onRequestDelete}>
                <div className="stack" style={{ gap: 'var(--sp-3)' }}>
                  <Textarea label="Reason for deletion" value={reason} onChange={(e) => setReason(e.target.value)} required />
                  <div className="row">
                    <Button type="submit" variant="danger" loading={requestDelete.isPending}>Request deletion</Button>
                    <Button type="button" variant="ghost" onClick={() => setShowDeleteForm(false)}>Cancel</Button>
                  </div>
                </div>
              </form>
            ) : (
              <Button variant="danger" onClick={() => setShowDeleteForm(true)}>Request deletion</Button>
            )}
          </CardBody>
        </Card>
      )}
    </>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="row" style={{ justifyContent: 'space-between' }}>
      <span style={{ color: 'var(--text-muted)' }}>{label}</span>
      <span style={{ fontWeight: 550 }}>{value}</span>
    </div>
  )
}
